import { deleteDB, openDB, type IDBPDatabase } from "idb";

import { PROTOCOL_VERSION, type SyncedTableName } from "@/lib/contract";

import type { RowChange } from "./store";
import {
  emptyTables,
  TABLE_NAMES,
  type PendingMutation,
  type Row,
  type SyncSession,
  type Tables,
} from "./types";

/**
 * The replica on disk.
 *
 * One database per person per workspace, so two people who share a browser
 * never see each other's data, and signing out deletes exactly one person's
 * copy.
 *
 * The database version is derived from the protocol version. When the wire
 * format changes, the browser runs an upgrade, which here means wiping every
 * store and starting again. A replica written by an older protocol is never
 * read by a newer client, and nobody has to write a migration for data the
 * server can simply resend.
 */

/** Bump when the shape of what is stored locally changes. */
const LOCAL_SCHEMA_VERSION = 1;

const DB_VERSION = LOCAL_SCHEMA_VERSION * 1000 + PROTOCOL_VERSION;

const META = "meta";
const PENDING = "pending";

const dbName = (session: SyncSession): string => `kanban:${session.workspaceId}:${session.userId}`;

export class Replica {
  private readonly db: IDBPDatabase;
  readonly name: string;

  private constructor(db: IDBPDatabase, name: string) {
    this.db = db;
    this.name = name;
  }

  static async open(session: SyncSession): Promise<Replica> {
    const name = dbName(session);
    const db = await openDB(name, DB_VERSION, {
      upgrade(database) {
        for (const store of Array.from(database.objectStoreNames)) {
          database.deleteObjectStore(store);
        }
        for (const table of TABLE_NAMES) database.createObjectStore(table, { keyPath: "id" });
        database.createObjectStore(PENDING, { keyPath: "id" });
        database.createObjectStore(META);
      },
      // Another tab is still on an older version and is blocking the upgrade.
      // Closing our handle when *we* are the old one lets the new tab proceed.
      blocking() {
        db.close();
      },
    });
    return new Replica(db, name);
  }

  async load(): Promise<{ base: Tables; pending: PendingMutation[]; lastVersion: number }> {
    const tx = this.db.transaction([...TABLE_NAMES, PENDING, META], "readonly");
    const base = emptyTables() as Record<SyncedTableName, Record<string, Row>>;

    await Promise.all(
      TABLE_NAMES.map(async (table) => {
        const rows = (await tx.objectStore(table).getAll()) as Row[];
        const byId: Record<string, Row> = {};
        for (const row of rows) byId[row.id] = row;
        base[table] = byId;
      }),
    );

    // Pending ids are UUIDv7, so key order is creation order: the queue comes
    // back in exactly the order the user made the changes.
    const pending = (await tx.objectStore(PENDING).getAll()) as PendingMutation[];
    const lastVersion =
      ((await tx.objectStore(META).get("lastVersion")) as number | undefined) ?? 0;
    await tx.done;

    return { base: base as Tables, pending, lastVersion };
  }

  /**
   * One transaction for the rows and the version together. If the tab closes
   * halfway, neither is written, so a stored version can never claim rows the
   * replica does not have.
   */
  async writeDelta(changes: readonly RowChange[], upTo: number): Promise<void> {
    const stores = [...new Set(changes.map((change) => change.table)), META];
    const tx = this.db.transaction(stores, "readwrite");
    for (const change of changes) {
      const store = tx.objectStore(change.table);
      for (const row of change.upserts) void store.put(row);
      for (const id of change.deletes) void store.delete(id);
    }
    void tx.objectStore(META).put(upTo, "lastVersion");
    await tx.done;
  }

  async putPending(mutation: PendingMutation): Promise<void> {
    await this.db.put(PENDING, mutation);
  }

  async deletePending(id: string): Promise<void> {
    await this.db.delete(PENDING, id);
  }

  close(): void {
    this.db.close();
  }

  /** Delete this replica entirely: on sign-out, and on a server-requested resync. */
  async destroy(): Promise<void> {
    this.db.close();
    await deleteDB(this.name);
  }
}

/** Remove a person's replica without opening it, as sign-out does. */
export async function deleteReplica(session: SyncSession): Promise<void> {
  await deleteDB(dbName(session));
}
